/**
 * Monaco editor wired to a {@link HostPlayground}-managed text file.
 *
 * The single shared MonacoEditor for playground-backed apps (Hearth, Bear).
 * Opens its file through `playground.open(path)` so every keystroke lands
 * via the playground's inode-stamping wrapper.
 *
 * Built-in features:
 *  - Bidirectional Automerge sync (inbound patches → Monaco edits; Monaco
 *    edits → granular Automerge splices) via {@link setupAutomergeSync}.
 *  - Optional vim keybindings (`vimMode`).
 *  - Optional pane-managed focus (`isFocused`).
 *  - Cross-file TypeScript autocomplete: the whole workspace is registered
 *    with Monaco by {@link registerWorkspaceTypes}, so `@/…` and mounted-package
 *    imports resolve, and an edit to another file surfaces in this file's
 *    typecheck without a reload.
 *
 * Bear/Hearth-specific extensions land via `onConfigureMonaco` (pre-mount
 * compiler/lib hooks) and `onMount` (post-mount editor instance — wire any
 * additional keybindings here).
 */
import { Editor, type Monaco } from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import { initVimMode } from "monaco-vim";
import type { AnyPlayFile, TextFile } from "playground/fs/types";
import type { HostPlayground } from "playground/hal/index";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Highlighter } from "shiki";

import { usePrefersDark } from "../lib/use-prefers-dark";
import { setupAutomergeSync } from "./automerge-monaco-sync";
import { useMonacoComments, type MonacoComments } from "./comment-layer";
import {
  installShiki,
  loadHighlighter,
  type EditorThemes,
} from "./shiki-themes";
import {
  applyWorkspaceDiagnostics,
  registerWorkspaceTypes,
} from "./workspace-types";

/**
 * A range in a file, in Monaco's own shape: what its editor service passes for
 * a navigation, and what its reveal APIs take. An end is optional — a plain
 * cursor position is a selection with no end.
 */
export interface EditorSelection {
  startLineNumber: number;
  startColumn: number;
  endLineNumber?: number;
  endColumn?: number;
}

export interface MonacoEditorProps {
  /**
   * Playground that owns this workspace's files. Host-side, because the editor
   * registers the workspace with TypeScript and needs to hear about changes to
   * it (see {@link registerWorkspaceTypes}).
   */
  playground: HostPlayground;
  /** Workspace-relative path; Monaco uses this as the editor's filename. */
  path: string;
  /** Identity attached to Automerge change metadata. */
  author?: string;
  /** Enable vim keybindings + status bar. */
  vimMode?: boolean;
  /** Syntax themes, picked by the OS color scheme. */
  themes: EditorThemes;
  /**
   * Pane-managed focus. When the embedder flips this to `true` the editor
   * focuses; when flipped to `false` the active DOM element inside the
   * editor blurs. Optional — Monaco's own focus model handles single-pane
   * usage without it.
   */
  isFocused?: boolean;
  /**
   * Open another workspace file, e.g. the target of "go to definition", at the
   * range the jump aimed for.
   *
   * The standalone editor can only navigate within the file it is showing:
   * its editor service looks for a model matching the requested resource, and
   * returns null for anything else, so a jump to another file silently does
   * nothing. Only the host knows how to show a second file (a tab, a split,
   * a pane), so it gets asked. Without this, definitions resolve and go
   * nowhere.
   *
   * The host is expected to hand `selection` back to whichever editor ends up
   * showing that path, as {@link MonacoEditorProps.revealSelection}.
   */
  onOpenPath?: (path: string, selection?: EditorSelection) => void;
  /**
   * Put the cursor here once the file is loaded, scrolling it into view.
   *
   * Identity is the trigger: a new object reveals, the same object does not,
   * so an ordinary re-render never yanks the cursor back from wherever the
   * reader has moved it.
   */
  revealSelection?: EditorSelection;
  /** Hook to add language-specific configuration before the editor mounts. */
  onConfigureMonaco?: (monaco: Monaco) => void;
  /** Notified once the editor is ready. */
  onMount?: (editor: editor.IStandaloneCodeEditor, monaco: Monaco) => void;
  className?: string;
  /** Comment threads to highlight, and the open one's card. */
  comments?: MonacoComments;
}

// Resolve a file path to a Monaco language id using Monaco's own language
// registry — every registered language declares the extensions it owns, so we
// get first-class support (highlighting, validation, formatting) for anything
// the bundled Monaco ships: JSON, Markdown, CSS, HTML, shell, YAML, etc.,
// without maintaining a bespoke extension map. Falls back to TypeScript for
// unknown or extensionless files, since this editor is primarily a TypeScript
// workspace.
function languageForPath(monaco: Monaco, path: string): string {
  const lower = path.toLowerCase();
  const dot = lower.lastIndexOf(".");
  const ext = dot >= 0 ? lower.slice(dot) : "";
  if (ext) {
    for (const lang of monaco.languages.getLanguages()) {
      if (lang.extensions?.some((e: string) => e.toLowerCase() === ext)) {
        return lang.id;
      }
    }
  }
  return "typescript";
}

function configureBaseTypeScript(monaco: Monaco) {
  const ts = monaco.languages.typescript;
  const compilerOptions = {
    target: ts.ScriptTarget.ESNext,
    module: ts.ModuleKind.ESNext,
    jsx: ts.JsxEmit.ReactJSX,
    moduleResolution: ts.ModuleResolutionKind.NodeJs,
    allowNonTsExtensions: true,
    baseUrl: "file:///",
    paths: { "@/*": ["*"] },
    strict: false,
    esModuleInterop: true,
    skipLibCheck: true,
  };
  ts.typescriptDefaults.setCompilerOptions(compilerOptions);
  ts.javascriptDefaults.setCompilerOptions(compilerOptions);
  applyWorkspaceDiagnostics(monaco);
}

/**
 * Editor-service shape we reach into. Monaco exposes no public hook for
 * "the user asked to open a different file", so the standalone editor's
 * private service is the seam, as it is in every editor embedding this.
 */
interface CodeEditorService {
  openCodeEditor: (
    input: {
      resource?: { path?: string };
      options?: { selection?: EditorSelection };
    },
    source: unknown,
    sideBySide?: boolean,
  ) => Promise<unknown>;
}

/** Marks a service instance as already wrapped: it is a shared singleton. */
const NAVIGATION_INSTALLED = Symbol.for("forest.monaco.crossFileNavigation");

/**
 * Let "go to definition" leave the current file.
 *
 * `StandaloneCodeEditorService.doOpenEditor` looks for a model matching the
 * requested resource and returns null when the editor is showing something
 * else, which is every cross-file jump. We take that null as "the host's turn"
 * and hand it the workspace path, so definitions land where the host puts
 * files. Returning `source` tells Monaco the request was handled.
 */
function installCrossFileNavigation(
  ed: editor.IStandaloneCodeEditor,
  onOpenPathRef: {
    current: ((path: string, selection?: EditorSelection) => void) | undefined;
  },
): void {
  const service = (ed as unknown as { _codeEditorService?: CodeEditorService })
    ._codeEditorService;
  if (!service) return;
  const marked = service as CodeEditorService & {
    [NAVIGATION_INSTALLED]?: true;
  };
  if (marked[NAVIGATION_INSTALLED]) return;
  marked[NAVIGATION_INSTALLED] = true;

  const openCodeEditor = service.openCodeEditor.bind(service);
  service.openCodeEditor = async (input, source, sideBySide) => {
    const result = await openCodeEditor(input, source, sideBySide);
    if (result !== null) return result;
    const resource = input?.resource?.path;
    const handler = onOpenPathRef.current;
    if (!resource || !handler) return result;
    // `file:///node_modules/bear/engine/factory.ts` → the workspace path. The
    // selection rides along, or the jump lands at the top of the file rather
    // than on the thing that was asked for.
    handler(resource.replace(/^\/+/, ""), input.options?.selection);
    return source;
  };
}

/** Loads the shared Shiki highlighter, then mounts the editor with it. */
export function MonacoEditor(props: MonacoEditorProps) {
  const [highlighter, setHighlighter] = useState<Highlighter | null>(null);
  useEffect(() => {
    void loadHighlighter().then(setHighlighter);
  }, []);
  return highlighter ? (
    <LoadedMonacoEditor {...props} highlighter={highlighter} />
  ) : null;
}

function LoadedMonacoEditor({
  highlighter,
  themes,
  playground,
  path,
  author,
  vimMode,
  isFocused,
  onOpenPath,
  revealSelection,
  onConfigureMonaco,
  onMount,
  className,
  comments,
}: MonacoEditorProps & { highlighter: Highlighter }) {
  // State, not a ref: Monaco loads asynchronously, and everything keyed off it
  // (the language id, the workspace type registration) has to run once it
  // arrives rather than on whatever the first render happened to see.
  const [monaco, setMonacoInstance] = useState<Monaco | null>(null);
  const dark = usePrefersDark();
  // Tracked as state (not just a ref) so the sync-setup effect re-runs when
  // the editor first becomes available. `@monaco-editor/react` keeps the
  // editor instance stable across `path` prop changes and only swaps the
  // underlying model — we re-install the listener on `textFile` changes
  // below so edits never get written to the previously open file.
  const [editor, setEditor] = useState<editor.IStandaloneCodeEditor | null>(
    null,
  );
  const [textFile, setTextFile] = useState<TextFile | null>(null);
  // Set for read-only files (mounted/managed content isn't an Automerge doc):
  // the model is seeded with this text and Monaco is read-only — no sync.
  const [readOnlyText, setReadOnlyText] = useState<string | null>(null);
  // Flips once the model actually holds the file's text. Seeding is async
  // (the Automerge read resolves after the state that schedules it), so
  // anything that positions the cursor must wait on this, not on `textFile`.
  const [contentReady, setContentReady] = useState(false);
  const statusBarRef = useRef<HTMLDivElement | null>(null);
  const authorRef = useRef(author);
  useEffect(() => {
    authorRef.current = author;
  }, [author]);
  // Held in a ref because the editor service is patched once, on mount, and
  // must keep calling whatever the host's current handler is.
  const onOpenPathRef = useRef(onOpenPath);
  useEffect(() => {
    onOpenPathRef.current = onOpenPath;
  }, [onOpenPath]);

  // Open the file through the playground. The returned PlayFile owns its
  // inode-stamping internally — write/patch/change/onChange call straight
  // through and the inode mtime/size update happens before they return.
  useEffect(() => {
    let cancelled = false;
    let opened: AnyPlayFile | null = null;
    setTextFile(null);
    setReadOnlyText(null);
    setContentReady(false);
    void playground.open(path).then(async (file) => {
      if (cancelled) {
        void file.close();
        return;
      }
      if (file.kind === "text") {
        // Automerge-backed → editable, with live bidirectional sync.
        opened = file;
        setTextFile(file);
        return;
      }
      // Managed/mounted content isn't an Automerge doc — there's nothing to
      // write to. Open it read-only: decode the blob into a static model.
      // Same editor, no sync — a real FS you just can't write to.
      try {
        const bytes = file.kind === "blob" ? await file.read() : null;
        if (!cancelled) {
          setReadOnlyText(
            bytes ? new TextDecoder().decode(bytes) : "[unsupported file]",
          );
        }
      } finally {
        void file.close();
      }
    });
    return () => {
      cancelled = true;
      void opened?.close();
    };
  }, [playground, path]);

  const handleBeforeMount = useCallback(
    (monaco: Monaco) => {
      setMonacoInstance(monaco);
      installShiki(monaco, highlighter);
      configureBaseTypeScript(monaco);
      onConfigureMonaco?.(monaco);
    },
    [highlighter, onConfigureMonaco],
  );

  const handleEditorDidMount = useCallback(
    (ed: editor.IStandaloneCodeEditor, monaco: Monaco) => {
      setMonacoInstance(monaco);
      setEditor(ed);
      installCrossFileNavigation(ed, onOpenPathRef);
      onMount?.(ed, monaco);
    },
    [onMount],
  );

  useEffect(() => {
    if (!editor || !textFile) return;
    let cancelled = false;
    let cleanup: (() => void) | undefined;
    // Seed the active model with the file's current text before attaching
    // the sync listener. Monaco's model registry caches models globally by
    // URI and `@monaco-editor/react` reuses a cached model on `path` swap
    // without re-applying `defaultValue`; the sync listener only forwards
    // *future* changes, so without this any text that arrived for this file
    // while a different one was open — or any text in a cached stale model
    // — would be left in the editor.
    void textFile.read().then((text) => {
      if (cancelled || !editor) return;
      const model = editor.getModel();
      if (model && model.getValue() !== text) model.setValue(text);
      setContentReady(true);
      cleanup = setupAutomergeSync(editor, textFile, {
        message: () =>
          authorRef.current
            ? JSON.stringify({ author: authorRef.current })
            : undefined,
      });
    });
    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [editor, textFile]);

  // Read-only files (mounted/managed): seed the model once and leave Monaco
  // read-only. No Automerge listener — there's no doc to sync to. Monaco infers
  // the language from `path`, so the source is still syntax-highlighted.
  useEffect(() => {
    if (!editor || readOnlyText === null) return;
    const model = editor.getModel();
    if (model && model.getValue() !== readOnlyText)
      model.setValue(readOnlyText);
    setContentReady(true);
  }, [editor, readOnlyText]);

  // Vim mode setup/teardown. Owned by the editor so consumers just flip a
  // boolean — no `onMount`-side wiring needed. The status bar div lives at
  // the bottom of the editor frame and only renders when vim is on.
  useEffect(() => {
    if (!editor || !vimMode || !statusBarRef.current) return;
    const vim = initVimMode(editor, statusBarRef.current);
    // `:w` is a no-op — edits already auto-sync via Automerge.
    (
      window as unknown as {
        MonacoVim?: { VimMode?: { Vim?: { defineEx: Function } } };
      }
    ).MonacoVim?.VimMode?.Vim?.defineEx("write", "w", () => {});
    return () => vim.dispose();
  }, [editor, vimMode]);

  // Cross-file TypeScript types: every workspace source, project files and
  // mounted packages alike, is registered with Monaco so `@/…` and `bear/…`
  // imports autocomplete and typecheck. The registry is shared by every editor
  // and watches the workspace itself, so mounting is the whole ask.
  useEffect(() => {
    if (!monaco) return;
    void registerWorkspaceTypes(monaco, playground).catch((error: unknown) => {
      console.error("Failed to register workspace types", error);
    });
  }, [monaco, playground]);

  // Land on what a navigation was aiming at. Gated on `contentReady` because
  // a jump into a file that isn't open yet arrives before its text does:
  // revealing against a still-empty model clamps the cursor to 1:1 and nothing
  // would re-trigger once the text lands. Monaco's own service does exactly
  // this pair of moves, so a jump within a file and a jump across one land
  // the same way.
  useEffect(() => {
    if (!editor || !revealSelection || !contentReady) return;
    const { startLineNumber, startColumn, endLineNumber, endColumn } =
      revealSelection;
    if (typeof endLineNumber === "number" && typeof endColumn === "number") {
      editor.setSelection({
        startLineNumber,
        startColumn,
        endLineNumber,
        endColumn,
      });
      editor.revealRangeInCenter({
        startLineNumber,
        startColumn,
        endLineNumber,
        endColumn,
      });
      return;
    }
    const position = { lineNumber: startLineNumber, column: startColumn };
    editor.setPosition(position);
    editor.revealPositionInCenter(position);
  }, [editor, revealSelection, contentReady]);

  // Pane-managed focus. Optional — only fires when the consumer wires it.
  const commentCard = useMonacoComments(editor, monaco, comments, contentReady);

  useEffect(() => {
    if (!editor || isFocused === undefined) return;
    if (isFocused) {
      editor.focus();
      return;
    }
    const domNode = editor.getDomNode();
    if (
      domNode &&
      document.activeElement &&
      domNode.contains(document.activeElement)
    ) {
      (document.activeElement as HTMLElement).blur();
    }
  }, [editor, isFocused]);

  return (
    <div
      className={
        className ?? "flex h-full flex-col flex-1 min-h-0 overflow-hidden"
      }
    >
      <Editor
        height="100%"
        path={path}
        language={monaco ? languageForPath(monaco, path) : undefined}
        defaultValue=""
        theme={dark ? themes.dark : themes.light}
        options={{
          fontSize: 14,
          fontFamily: "ui-monospace, monospace",
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          tabSize: 2,
          automaticLayout: true,
          readOnly: readOnlyText !== null,
          padding: { top: 8 },
        }}
        beforeMount={handleBeforeMount}
        onMount={handleEditorDidMount}
      />
      {commentCard}
      {vimMode ? (
        <div
          ref={statusBarRef}
          className="h-6 border-t border-border bg-muted px-2 text-xs font-mono leading-6 text-muted-foreground"
        />
      ) : null}
    </div>
  );
}
